import React, { useEffect, useRef } from 'react';
import '../../../components/ConfirmModal/styles.css';
import '../styles/CustomSizeModal.css';

const CustomSizeModal = ({
  isOpen,
  loading,
  canSubmit,
  widthValue,
  heightValue,
  unitValue,
  nameValue,
  errors,
  quickSizes,
  onWidthChange,
  onHeightChange,
  onUnitChange,
  onNameChange,
  onQuickSize,
  onCancel,
  onConfirm,
}) => {
  const modalRef = useRef(null);
  const widthRef = useRef(null);

  useEffect(() => {
    if (isOpen && widthRef.current) {
      widthRef.current.focus();
      widthRef.current.select();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleOverlayClick = () => {
    if (!loading && onCancel) {
      onCancel();
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Escape' && !loading) {
      event.preventDefault();
      onCancel();
      return;
    }

    if (event.key === 'Enter' && canSubmit && !loading) {
      event.preventDefault();
      onConfirm();
      return;
    }

    if (event.key !== 'Tab' || !modalRef.current) {
      return;
    }

    const focusable = modalRef.current.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    const focusableElements = Array.from(focusable).filter(
      (el) => !el.disabled,
    );
    if (focusableElements.length === 0) {
      return;
    }
    const first = focusableElements[0];
    const last = focusableElements[focusableElements.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const normalizeDimension = (value) => {
    if (value === '' || value === null || value === undefined) {
      return '';
    }
    const parsed = Math.round(Number(value));
    if (Number.isNaN(parsed)) {
      return value;
    }
    return String(parsed);
  };

  return (
    <div className="confirm-modal-overlay" onClick={handleOverlayClick}>
      <div
        className="confirm-modal-container custom-size-modal"
        onClick={(event) => event.stopPropagation()}
        onKeyDown={handleKeyDown}
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="custom-size-title"
      >
        <div className="confirm-modal-content">
          <h2 className="confirm-modal-title" id="custom-size-title">
            Custom Size
          </h2>
          <p className="confirm-modal-message">
            Create your own dimensions to match any campaign.
          </p>

          <div className="custom-size-quick">
            {quickSizes.map((size) => (
              <button
                key={`${size.width}x${size.height}`}
                type="button"
                className="custom-size-chip"
                onClick={() => onQuickSize(size)}
                disabled={loading}
              >
                {size.width}×{size.height}
              </button>
            ))}
          </div>

          <div className="custom-size-fields">
            <label className="custom-size-field">
              <span>Width</span>
              <input
                ref={widthRef}
                type="number"
                min="100"
                max="8000"
                step="1"
                value={widthValue}
                onChange={(event) => onWidthChange(event.target.value)}
                onBlur={(event) =>
                  onWidthChange(normalizeDimension(event.target.value))
                }
                disabled={loading}
                className="custom-size-input"
              />
              {errors?.width && (
                <span className="custom-size-error">{errors.width}</span>
              )}
            </label>
            <label className="custom-size-field">
              <span>Height</span>
              <input
                type="number"
                min="100"
                max="8000"
                step="1"
                value={heightValue}
                onChange={(event) => onHeightChange(event.target.value)}
                onBlur={(event) =>
                  onHeightChange(normalizeDimension(event.target.value))
                }
                disabled={loading}
                className="custom-size-input"
              />
              {errors?.height && (
                <span className="custom-size-error">{errors.height}</span>
              )}
            </label>
            <label className="custom-size-field">
              <span>Unit</span>
              <select
                value={unitValue}
                onChange={(event) => onUnitChange(event.target.value)}
                disabled={loading}
                className="custom-size-select"
              >
                <option value="px">px</option>
              </select>
            </label>
            <label className="custom-size-field full">
              <span>Design name</span>
              <input
                type="text"
                value={nameValue}
                onChange={(event) => onNameChange(event.target.value)}
                disabled={loading}
                className="custom-size-input"
              />
              {errors?.name && (
                <span className="custom-size-error">{errors.name}</span>
              )}
            </label>
          </div>

          <div className="confirm-modal-buttons custom-size-actions">
            <button
              type="button"
              className="confirm-modal-btn confirm-btn"
              onClick={onConfirm}
              disabled={!canSubmit || loading}
            >
              {loading ? 'Creating...' : 'Create design'}
            </button>
            <button
              type="button"
              className="confirm-modal-btn cancel-btn"
              onClick={onCancel}
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomSizeModal;
